import { Component, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { Observable, filter, map, of } from 'rxjs';
import { environment } from 'src/environment';
import { extService } from 'src/models/extService';
import { NotificationBar } from 'src/services/notification.service';
import { authNeeded } from '../app-routing.module';
import { emsCompetition, emsTeam, emsWorkspace } from 'src/models/emsModels';
import { MatSelectChange } from '@angular/material/select';
import { WorkspaceCreateDialogComponent } from '../workspace-create-dialog/workspace-create-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent {
  profilePicture = "";
  username = "";
  isSignedIn = false;
  isAuthUrl = false;
  isDarkThemeOn = false;
  isAdmin = false;
  isSupport = false;
  isPartner = false;
  isPremium = false;
  notificationBar: NotificationBar;
  extServices = Array<extService>();
  emsWorkspaces = Array<emsWorkspace>();
  currentWorkspaceID = "";
  workspaceDropdown = "";
  hasSubscription = false;
  hasStageEnabled = false;
  hasBadgesEnabled = false;
  isOwner = false;
  userID = "";
  isLoading : Observable<boolean> = of(true);
  isLoadingEmsWorkspaceIDs = true;
  competitions = Array<emsCompetition>();
  teams = Array<emsTeam>();
  competitionsDBRef: any;
  teamsDBRef: any;
  userWorkspaceDBRef: any;

  @ViewChild('sidenav') sidenav!: MatSidenav;

  constructor(private router: Router, private fireAuth: AngularFireAuth, private db: AngularFireDatabase, private snackBar: MatSnackBar, private location: Location, private activatedRoute: ActivatedRoute, public dialog: MatDialog) {
    this.notificationBar = new NotificationBar(snackBar);
  }
   
  ngOnInit() {
    this.isLoading = this.router.events.pipe(filter( event =>
      event instanceof NavigationStart || event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError
    ),map( event => event instanceof NavigationStart));
    this.fireAuth.onAuthStateChanged(() => {
      this.checkIfSignedIn();
    });
    this.loadTheme();
    this.loadExtServices();
  }

  ngOnDestroy() {
    if (this.competitionsDBRef) {
      this.competitionsDBRef.off('value');
    };
    if (this.teamsDBRef) {
      this.teamsDBRef.off('value');
    };
    if (this.userWorkspaceDBRef) {
      this.userWorkspaceDBRef.off('value');
    };
  }

  async loadExtServices() {
    var tempServicesDBRef = this.db.database.ref('/services');
    await tempServicesDBRef.once('value').then( x => {
      x.forEach( x => {
        var extService = x.toJSON() as extService;
        this.extServices.push(extService);
      })
    })
    this.extServices.sort((a, b) => a.position - b.position);
  }

  loadTheme() {
    // Always Dark Mode
    localStorage.setItem('theme', 'dark');

    var savedTheme = localStorage.getItem('theme');
    if (savedTheme == null) {
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        localStorage.setItem('theme', 'dark');
        this.toggleTheme();
      }
      else {
        localStorage.setItem('theme', 'light');
      }
    }
    else if (savedTheme == "dark") {
      this.toggleTheme();
    }
  }

  toggleTheme() {
    if (this.isDarkThemeOn) {
      document.body.classList.remove('dark-mode');
      this.isDarkThemeOn = false;
      localStorage.setItem('theme', 'light');
    } 
    else {
      document.body.classList.add('dark-mode');
      this.isDarkThemeOn = true;
      localStorage.setItem('theme', 'dark');
    }
  }

  async checkIfSignedIn() {
    var user = await (this.fireAuth.currentUser);
    if (user && !this.isAuthUrl) {
      this.isSignedIn = true;
      this.profilePicture = user.photoURL;
      this.username = user.displayName;
      this.userID = user.uid;
      this.getWorkspaces(user.uid);
      user.getIdTokenResult().then( idTokenResult => {
        this.isAdmin = idTokenResult.claims["admin"];
        this.isSupport = idTokenResult.claims["support"];
        this.isPartner = idTokenResult.claims["partner"];
        this.isPremium = idTokenResult.claims["premium"];
      });
    } 
    else {
      this.isSignedIn = false;
      this.isAdmin = false;
      this.isSupport = false;
      this.isPartner = false;
      this.isPremium = false;
      this.emsWorkspaces = null;
      this.userID = "";
    }
  }

  signIn() {
    var currentPage = window.location.href;
    window.location.href = environment.authUrl + currentPage;
  }

  signOut() {
    var currentPath = window.location.pathname;
    if (authNeeded.some(route => currentPath.includes(route))) {   
      this.router.navigate(['']);
    } 
    this.fireAuth.signOut().then( () => {  
      this.currentWorkspaceID = "";
      this.competitions = new Array<emsCompetition>();
      this.teams = new Array<emsTeam>();
      this.notificationBar.show("Signed out!")
    }, err => {
      this.notificationBar.showUnknownError()
    })
  }

  getWorkspaces(userID) {
    if (this.isSignedIn == true) {
      var currentPath = this.location.path().split("/")[1];
      this.userWorkspaceDBRef = this.db.database.app.database(environment.emsDatabaseURL).ref('/user/' + userID + '/workspaces/');
      this.userWorkspaceDBRef.on('value', value => {
        var workspacePromises = [];
        this.emsWorkspaces = [];
        value.forEach( userWorkspace => {
          if (userWorkspace.val() == true) {
            var tempWorkspaceDBRef = this.db.database.app.database(environment.emsDatabaseURL).ref('/workspaces/' + userWorkspace.key);
            var workspacePromise = tempWorkspaceDBRef.once("value").then( workspace => {
              var emsWorkspace = workspace.toJSON() as emsWorkspace;
              this.emsWorkspaces.push({uid: workspace.key, name: emsWorkspace.name, owner: emsWorkspace.owner, subscription: emsWorkspace.subscription, features: emsWorkspace.features});
              if (workspace.key == currentPath) {
                this.currentWorkspaceID = workspace.key;  
                this.loadCompetitions();    
                this.loadTeams();          
              }
            })
            workspacePromises.push(workspacePromise);
          }
        })
        Promise.all(workspacePromises).then(() => {
          this.emsWorkspaces.sort(function(a, b) {
            var textA = a.name.toUpperCase();
            var textB = b.name.toUpperCase();
            return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
          });
          this.isLoadingEmsWorkspaceIDs = false;
          if (this.emsWorkspaces.length != 0 && this.emsWorkspaces.findIndex(x => x.uid == this.location.path().split("/")[1]) == -1 && this.location.path().split("/")[2]) {
            var selectWorkspace = new MatSelectChange(null, "")
            this.changeWorkspace(selectWorkspace)
          }
          else if (this.emsWorkspaces.length == 1) {
            var selectWorkspace = new MatSelectChange(null, this.emsWorkspaces[0].uid)
            this.changeWorkspace(selectWorkspace)
          }
          else if (this.currentWorkspaceID != "") {
            var selectWorkspace = new MatSelectChange(null, this.currentWorkspaceID)
            this.changeWorkspace(selectWorkspace)
          }
          else if (this.emsWorkspaces.length > 1) {
            var selectWorkspace = new MatSelectChange(null, this.emsWorkspaces[0].uid)
            this.changeWorkspace(selectWorkspace)
          }
        });
      })
    }
  }

  changeWorkspace(event: MatSelectChange) {
    this.currentWorkspaceID = event.value;
    this.loadCompetitions();
    this.loadTeams();
    this.workspaceDropdown = event.value
    if (this.emsWorkspaces.find(x => x.uid == event.value)?.owner == this.userID) {
      this.isOwner = true;
    }
    else {
      this.isOwner = false;
    }
    if (this.emsWorkspaces.find(x => x.uid == event.value)?.subscription) {
      this.hasSubscription = true;
    }
    else {
      this.hasSubscription = false;
    }
    if (this.hasSubscription == false && event.value != "") {
      var currentQueryParams = this.activatedRoute.snapshot.queryParams;
      this.router.navigate(['/' + event.value + '/settings'], {queryParams: currentQueryParams});
    }
    else {
      this.hasStageEnabled = this.emsWorkspaces.find(x => x.uid == event.value)?.features?.['stage'];
      this.hasBadgesEnabled = this.emsWorkspaces.find(x => x.uid == event.value)?.features?.['badges'];
      var currentPath = this.location.path().split("/");
      if (currentPath.length <= 2) {
        if (authNeeded.some(route => currentPath.includes(route))) {
          this.router.navigate([event.value, 'dashboard'])
        }
      } 
      else {
        this.router.routeReuseStrategy.shouldReuseRoute = () => false;
        // this.router.navigate([event.value, currentPath[2]]) // OLD - 09.07.26 changed
        this.router.navigate([event.value, ...currentPath.slice(2)]); // NEW
      }
    }
  }

  async createWorkspace() {
    var dialogRef = this.dialog.open(WorkspaceCreateDialogComponent, {
      data: { }
    });
    var res = await dialogRef.afterClosed().toPromise();
    if (res != false) {
      this.router.navigate([res, 'settings']).then(result => {
        this.sidenav.close();
        this.workspaceDropdown = res;
       });
    }
  }

  loadCompetitions() {
    if (this.competitionsDBRef) {
      this.competitionsDBRef.off('value');
    };
    this.competitionsDBRef = this.db.database.app.database(environment.emsDatabaseURL).ref('/competitions').orderByChild('workspace').equalTo(this.currentWorkspaceID);
    this.competitionsDBRef.on('value', x => {
      this.competitions = new Array<emsCompetition>();
      x.forEach( x => {
        var competition = x.toJSON() as emsCompetition;
        if (competition.workspace == this.currentWorkspaceID) {
          competition.uid = x.key;
          this.competitions.push(competition)
        }
      });
      this.competitions.sort((a, b) => a.name.localeCompare(b.name));
    });
  }

  loadTeams() {
    if (this.teamsDBRef) {
      this.teamsDBRef.off('value');
    };
    this.teamsDBRef = this.db.database.app.database(environment.emsDatabaseURL).ref('/teams').orderByChild('workspace').equalTo(this.currentWorkspaceID);
    this.teamsDBRef.on('value', x => {
      this.teams = new Array<emsTeam>();
      x.forEach( x => {
        var team = x.toJSON() as emsTeam;
        if (team.workspace == this.currentWorkspaceID) {
          team.uid = x.key;
          this.teams.push(team)
        }
      });
      this.teams.sort((a, b) => a.name.localeCompare(b.name));
    });
  }
}
